import { cloudinaryUrl } from '@realadvisor/cloudinary';

import type { ToolPart } from './ApprovalActions';

// CRM photos shown by Aura (crm-chat-api tools/crm-images.ts). The tool
// returns the URLs as stored; the grid shows imgproxy thumbnails and only the
// lightbox loads the full image. Pure helpers, importable from node tests.

export const SHOW_CRM_IMAGES_TOOL_NAME = 'show_crm_images';

export type CrmImage = {
  id: string;
  title: string | null;
  // imgproxy thumbnail for the grid, never the stored original.
  thumbnailUrl: string;
  // Full size, through imgproxy as in the listing gallery (HEIC and WebP
  // originals come out as JPEG).
  fullUrl: string;
};

export type ShownCrmImages = {
  title: string | null;
  images: Array<CrmImage>;
  // Ids the user cannot read, or deleted: the chat API answers both alike.
  missing: Array<string>;
  // Photos the card could not show (not on RealAdvisor image storage).
  skipped: number;
};

const THUMBNAIL_OPTIONS = { w: 320, h: 240, c: 'fill', f: 'jpg' } as const;

// cloudinaryUrl hands back the source untouched when imgproxy cannot read it:
// such a URL is never put in the grid.
export const crmImageThumbnailUrl = (url: string): string | null => {
  const thumbnail = cloudinaryUrl(THUMBNAIL_OPTIONS, url);
  return thumbnail === '' || thumbnail === url ? null : thumbnail;
};

export const crmImageFullUrl = (url: string): string =>
  cloudinaryUrl({ f: 'jpg' }, url);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value != null && !Array.isArray(value);

const strings = (value: unknown): Array<string> =>
  Array.isArray(value)
    ? value.filter((item): item is string => typeof item === 'string')
    : [];

type Row = { id: string; url: string; title: string | null; order: number };

const rowOf = (value: unknown): Row | null => {
  if (
    !isRecord(value) ||
    typeof value.id !== 'string' ||
    typeof value.url !== 'string'
  ) {
    return null;
  }
  const title = typeof value.title === 'string' ? value.title.trim() : '';
  return {
    id: value.id,
    url: value.url,
    title: title === '' ? null : title,
    order:
      typeof value.order === 'number' ? value.order : Number.MAX_SAFE_INTEGER,
  };
};

// What a finished show_crm_images call put on screen, or null while it runs
// or when it failed (`{ error }`): then the call stays a regular step.
export const shownCrmImagesOf = (part: ToolPart): ShownCrmImages | null => {
  if (
    part.toolName !== SHOW_CRM_IMAGES_TOOL_NAME ||
    part.state !== 'output-available' ||
    !isRecord(part.output) ||
    !Array.isArray(part.output.images)
  ) {
    return null;
  }
  const rows = part.output.images
    .flatMap(item => rowOf(item) ?? [])
    .sort((a, b) => a.order - b.order);
  const images = rows.flatMap((row): Array<CrmImage> => {
    const thumbnailUrl = crmImageThumbnailUrl(row.url);
    return thumbnailUrl == null
      ? []
      : [
          {
            id: row.id,
            title: row.title,
            thumbnailUrl,
            fullUrl: crmImageFullUrl(row.url),
          },
        ];
  });
  const title = part.output.title;
  return {
    title: typeof title === 'string' && title.trim() !== '' ? title : null,
    images,
    missing: strings(part.output.missing),
    skipped:
      strings(part.output.skipped).length + (rows.length - images.length),
  };
};
